import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Quickdraw — reflex react-to-cue, redesigned for 3D depth + engagement.
 * Game 07 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: seed-driven cue schedule + (idx, dir,
 * latency_ms) events replay unchanged. Visual layer rebuilt: 3D arrows
 * zooming in from the corresponding lane, lane glow during cue, streak-
 * driven environment intensity, central node feedback.
 */

export const CUES_PER_RUN = 30;
export const WINDOW_MS = 300;
const INTER_CUE_MIN_MS = 1500;
const INTER_CUE_MAX_MS = 4000;

export type Dir = 0 | 1 | 2 | 3;

export interface QuickdrawEvent { idx: number; dir: -1 | Dir; latency_ms: number; }
interface Cue { idx: number; show_t_ms: number; dir: Dir; combo_dir?: Dir; }
const COMBO_EVERY_CLIENT = 5;
const COMBO_BONUS_CLIENT = 200;
const STANDOFF_EVERY_CLIENT = 10;
const STANDOFF_DELAY_MS_CLIENT = 800;
const STANDOFF_BONUS_CLIENT = 600;
const PHRASE_LENGTH_CLIENT = 5;
const PHRASE_BONUS_CLIENT = 250;
function isComboIdx(idx: number): boolean {
  return idx >= COMBO_EVERY_CLIENT - 1 && (idx + 1) % COMBO_EVERY_CLIENT === 0;
}
function isStandoffIdx(idx: number): boolean {
  return idx > 0 && (idx + 1) % STANDOFF_EVERY_CLIENT === 0;
}

export interface QuickdrawCallbacks {
  onHud: (cuesDone: number, lastBucket: string, scoreEstimate: number) => void;
  onGameOver: (result: {
    events: QuickdrawEvent[];
    scoreEstimate: number;
    bestStreak: number;
    bucketsByName: Record<string, number>;
    combosHit: number;
    standoffsHit: number;
    phrasesCompleted: number;
    /** v5(d): set on the first non-miss input that lands in a cue
     *  window. "keyboard" if the first such input came from onKey,
     *  "swipe" if from onPointer. null if no qualifying input fired
     *  (zero-event run). Per dossier 07-quickdraw/07-telemetry.md §3. */
    inputModality: "keyboard" | "swipe" | null;
    /** v5(d): performance.now() at the first qualifying input — i.e.
     *  ms since page-load to first effective tap. null if no input
     *  fired. Per dossier §3 Q6. */
    firstInputDelayMs: number | null;
  }) => void;
}

export interface QuickdrawOptions {
  /**
   * v5(c): when true, render the cue as a procedural curved-line arrow
   * (THREE.Line, no fill) instead of the extruded-chevron MeshStandard
   * arrow. Strictly cosmetic — bucket math, latency timing, and the
   * 300 ms window are unchanged. The setting is read once at
   * construction; changing it mid-run has no effect.
   */
  proceduralGlyph?: boolean;
}

/**
 * v5(c) — build a curved arrow as a polyline in the local XY plane with
 * the tip at +y. Callers apply the same world rotation the extruded
 * arrow uses (x = -π/2 to lay the shape on the XZ plane + z = atan2
 * for direction), so the line stays drop-in compatible with the
 * existing cue transform.
 */
function buildCurvedArrowLine(): THREE.Line {
  const pts: THREE.Vector3[] = [];
  const STEPS = 28;
  const radius = 0.85;
  // Sweep arc: from roughly the lower-left of the tip out to the tip.
  const a0 = -Math.PI * 0.55;
  const a1 = Math.PI * 0.5;
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS;
    const angle = a0 + (a1 - a0) * t;
    pts.push(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
  }
  // Arrowhead chevron at the tip. Use the curve's local tangent so the
  // head aligns with the direction of travel, not a hard-coded axis.
  const tip = pts[pts.length - 1].clone();
  const prev = pts[pts.length - 5];
  const tangent = new THREE.Vector3().subVectors(tip, prev).normalize();
  const perp = new THREE.Vector3(-tangent.y, tangent.x, 0);
  const HEAD_LEN = 0.36;
  const HEAD_W = 0.22;
  const back = tip.clone().sub(tangent.clone().multiplyScalar(HEAD_LEN));
  pts.push(back.clone().add(perp.clone().multiplyScalar(HEAD_W)));
  pts.push(tip.clone());
  pts.push(back.clone().sub(perp.clone().multiplyScalar(HEAD_W)));
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  // LineBasicMaterial has no .emissive — we drive the "glow" via a
  // bright color value picked up by the composer's bloom pass.
  const mat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true });
  return new THREE.Line(geo, mat);
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function generateCues(seed: number): Cue[] {
  const rng = makeRng(seed);
  const out: Cue[] = [];
  let t = 0;
  for (let i = 0; i < CUES_PER_RUN; i++) {
    t += INTER_CUE_MIN_MS + rng() * (INTER_CUE_MAX_MS - INTER_CUE_MIN_MS);
    if (isStandoffIdx(i)) t += STANDOFF_DELAY_MS_CLIENT;
    const dir = Math.floor(rng() * 4) as Dir;
    if (isComboIdx(i)) {
      const combo_dir = Math.floor(rng() * 4) as Dir;
      out.push({ idx: i, show_t_ms: Math.round(t), dir, combo_dir });
    } else {
      rng(); // burn for determinism parity with server
      out.push({ idx: i, show_t_ms: Math.round(t), dir });
    }
  }
  return out;
}

// Dir → unit vector in XZ plane. 0=N (-z), 1=E (+x), 2=S (+z), 3=W (-x)
const DIR_VEC: [number, number][] = [[0, -1], [1, 0], [0, 1], [-1, 0]];
const DIR_COLOR = [0x66ddff, 0xffd54a, 0xff5b3a, 0xa0ffea];
const DIR_EMISSIVE = [0x0080cc, 0xff8800, 0xff2010, 0x46ffe0];

interface LaneVisual { track: THREE.Mesh; trackMat: THREE.MeshBasicMaterial; }

export class Quickdraw {
  private engine: Engine;
  private audio = new AudioBed();
  private cues: Cue[];
  private arrowMesh: THREE.Mesh;
  private arrowMat: THREE.MeshStandardMaterial;
  /** v5(c) — alternative procedural-line glyph; null unless opt-in. */
  private glyphLine: THREE.Line | null = null;
  private glyphLineMat: THREE.LineBasicMaterial | null = null;
  private proceduralGlyph: boolean;
  private centerNode: THREE.Mesh;
  private centerMat: THREE.MeshStandardMaterial;
  private lanes: LaneVisual[] = [];

  private startedAt = 0;
  private currentCue = 0;
  private currentCueShown = false;
  private currentCueShowMs = 0;
  private currentCueAcked = false;
  // v5(d) — onboarding signals. Set on the first non-miss input that
  // lands inside a cue window. Per dossier 07-quickdraw/07-telemetry.md
  // §3. The submit handler accepts these (v5(b) commit c6f04bb wired
  // it server-side); this class fills them in now.
  private inputModality: "keyboard" | "swipe" | null = null;
  private firstInputDelayMs: number | null = null;
  // v3 combo state — how many directional inputs received for the
  // current cue + the first tap's bucket (for bonus on completion).
  private currentCueTaps = 0;
  private currentCueFirstBucket: "lightning" | "quick" | "clean" | "late" | "miss" | null = null;
  private combosHit = 0;
  private standoffsHit = 0;
  private phrasesCompleted = 0;
  private phraseStreak = 0;
  private events: QuickdrawEvent[] = [];
  private streak = 0;
  private bestStreak = 0;
  private scoreEstimate = 0;
  private done = false;
  private cb: QuickdrawCallbacks;
  private feedbackPulse = 0;
  private feedbackColor = 0xffffff;
  private envLight: THREE.PointLight;

  private onKey = (e: KeyboardEvent) => this.handleInput(this.keyToDir(e.code), "keyboard");

  constructor(canvas: HTMLCanvasElement, cb: QuickdrawCallbacks, seed = Math.floor(Math.random() * 0x7fffffff), options: QuickdrawOptions = {}) {
    this.cb = cb;
    this.cues = generateCues(seed);
    this.proceduralGlyph = options.proceduralGlyph === true;

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    this.engine.camera.position.set(0, -14, 14);
    this.engine.camera.lookAt(0, 2, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x050614, 12, 36);
    this.engine.scene.background = new THREE.Color(0x050614);

    const ambient = new THREE.AmbientLight(0x506080, 0.5);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.9);
    key.position.set(6, 12, 8);
    this.engine.scene.add(key);
    this.envLight = new THREE.PointLight(0x66ddff, 0.6, 18, 1.5);
    this.envLight.position.set(0, 3, 0);
    this.engine.scene.add(this.envLight);

    // Floor disc.
    const floorGeo = new THREE.CircleGeometry(14, 64);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x020410, roughness: 0.92, metalness: 0.18 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    this.engine.scene.add(floor);

    // Four lanes radiating out from center as wide bars on the floor.
    for (let i = 0; i < 4; i++) {
      const [dx, dz] = DIR_VEC[i];
      const trackGeo = new THREE.BoxGeometry(0.7, 0.1, 9);
      const trackMat = new THREE.MeshBasicMaterial({ color: DIR_COLOR[i], transparent: true, opacity: 0.15, blending: THREE.AdditiveBlending });
      const track = new THREE.Mesh(trackGeo, trackMat);
      track.position.set(dx * 6, -0.4, dz * 6);
      track.rotation.y = Math.atan2(dx, dz);
      this.engine.scene.add(track);
      this.lanes.push({ track, trackMat });
    }

    // Center node — bright when cue is shown, dims after ack.
    const cGeo = new THREE.IcosahedronGeometry(0.9, 1);
    this.centerMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x444466, emissiveIntensity: 0.6, roughness: 0.3, metalness: 0.6 });
    this.centerNode = new THREE.Mesh(cGeo, this.centerMat);
    this.centerNode.position.set(0, 0.7, 0);
    this.engine.scene.add(this.centerNode);

    // Big 3D arrow that zooms toward center when a cue fires. Reused across cues.
    const arrowShape = new THREE.Shape();
    arrowShape.moveTo(0, 0.9);
    arrowShape.lineTo(0.6, -0.3);
    arrowShape.lineTo(0.2, -0.3);
    arrowShape.lineTo(0.2, -0.9);
    arrowShape.lineTo(-0.2, -0.9);
    arrowShape.lineTo(-0.2, -0.3);
    arrowShape.lineTo(-0.6, -0.3);
    arrowShape.lineTo(0, 0.9);
    const arrowGeo = new THREE.ExtrudeGeometry(arrowShape, { depth: 0.18, bevelEnabled: false });
    arrowGeo.center();
    this.arrowMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x88ccff, emissiveIntensity: 1.5, roughness: 0.3, metalness: 0.5 });
    this.arrowMesh = new THREE.Mesh(arrowGeo, this.arrowMat);
    this.arrowMesh.scale.setScalar(0);
    this.engine.scene.add(this.arrowMesh);

    // v5(c) — opt-in procedural-line glyph. We always build both meshes
    // so the toggle has zero load cost on the line path, but only one is
    // ever visible in a given run.
    if (this.proceduralGlyph) {
      this.arrowMesh.visible = false;
      this.glyphLine = buildCurvedArrowLine();
      this.glyphLineMat = this.glyphLine.material as THREE.LineBasicMaterial;
      this.glyphLine.scale.setScalar(0);
      this.engine.scene.add(this.glyphLine);
    }

    window.addEventListener("keydown", this.onKey);
    canvas.addEventListener("pointerdown", this.onPointer);
  }

  private keyToDir(code: string): -1 | Dir {
    if (code === "ArrowUp" || code === "KeyW") return 0;
    if (code === "ArrowRight" || code === "KeyD") return 1;
    if (code === "ArrowDown" || code === "KeyS") return 2;
    if (code === "ArrowLeft" || code === "KeyA") return 3;
    return -1;
  }

  private touchStart = { x: 0, y: 0, t: 0 };
  private onPointer = (e: PointerEvent) => {
    this.touchStart = { x: e.clientX, y: e.clientY, t: performance.now() };
    const up = (eu: PointerEvent) => {
      window.removeEventListener("pointerup", up);
      const dx = eu.clientX - this.touchStart.x;
      const dy = eu.clientY - this.touchStart.y;
      const mag = Math.hypot(dx, dy);
      if (mag < 30 || performance.now() - this.touchStart.t > 500) return;
      let dir: -1 | Dir;
      if (Math.abs(dx) > Math.abs(dy)) dir = dx > 0 ? 1 : 3;
      else dir = dy > 0 ? 2 : 0;
      this.handleInput(dir, "swipe");
    };
    window.addEventListener("pointerup", up);
  };

  private handleInput(dir: -1 | Dir, modality: "keyboard" | "swipe") {
    if (this.done || dir === -1) return;
    if (!this.currentCueShown || this.currentCueAcked) return;
    const now = performance.now() - this.startedAt;
    const latencyMs = now - this.currentCueShowMs;
    if (latencyMs > WINDOW_MS) return;
    // v5(d): capture onboarding signals on the FIRST qualifying input
    // (one that survives the early returns above). Subsequent inputs
    // don't overwrite. firstInputDelayMs is ms-since-page-load via
    // performance.now() (which has timeOrigin === navigation start).
    if (this.firstInputDelayMs === null) {
      this.firstInputDelayMs = Math.round(performance.now());
      this.inputModality = modality;
    }
    const cue = this.cues[this.currentCue];
    const isCombo = isComboIdx(cue.idx);
    const maxTaps = isCombo ? 2 : 1;
    // Determine which direction this tap is expected to match.
    const expectedDir = isCombo && this.currentCueTaps === 1 ? (cue.combo_dir as Dir) : cue.dir;
    const correct = dir === expectedDir;
    this.currentCueTaps += 1;
    this.recordTap(cue.idx, dir, latencyMs, correct, isCombo);
    if (this.currentCueTaps >= maxTaps) {
      this.currentCueAcked = true;
      this.hideCue();
    }
  }

  /** Per-tap scoring — does NOT advance to the next cue. Combo cues
   *  call this twice before hideCue() runs. */
  private recordTap(idx: number, dir: -1 | Dir, latencyMs: number, correct: boolean, isCombo: boolean) {
    this.events.push({ idx, dir, latency_ms: Math.round(latencyMs) });
    const bucket = !correct || latencyMs < 0 || latencyMs > WINDOW_MS
      ? "miss"
      : latencyMs <= 120 ? "lightning"
      : latencyMs <= 180 ? "quick"
      : latencyMs <= 240 ? "clean"
      : "late";
    const isStandoff = isStandoffIdx(idx);
    if (bucket === "miss") {
      this.streak = 0;
      this.phraseStreak = 0;
      this.audio.thud();
      this.feedbackPulse = 1;
      this.feedbackColor = 0xff5b3a;
    } else {
      this.streak += 1;
      if (this.streak > this.bestStreak) this.bestStreak = this.streak;
      const detune = bucket === "lightning" ? 12 : bucket === "quick" ? 7 : bucket === "clean" ? 4 : 0;
      this.audio.chime(detune, 0);
      this.feedbackPulse = bucket === "lightning" ? 1.4 : 1.0;
      this.feedbackColor = bucket === "lightning" ? 0xffd54a : DIR_COLOR[(dir === -1 ? 0 : dir)];
      // v4: phrase tracking client-side.
      this.phraseStreak += 1;
      if (this.phraseStreak >= PHRASE_LENGTH_CLIENT) {
        this.phrasesCompleted += 1;
        this.scoreEstimate += PHRASE_BONUS_CLIENT;
        this.phraseStreak = 0;
        this.audio.chime(24, 0.4);
      }
    }
    let weight = bucket === "lightning" ? 500 : bucket === "quick" ? 300 : bucket === "clean" ? 200 : bucket === "late" ? 100 : 0;
    if (isStandoff && bucket === "lightning") weight = Math.round(weight * 2.0);
    const mult = 1 + Math.min(this.streak, 30) * 0.05;
    this.scoreEstimate += Math.round(weight * mult);
    // v4: standoff bonus on non-miss.
    if (isStandoff && bucket !== "miss") {
      this.scoreEstimate += STANDOFF_BONUS_CLIENT;
      this.standoffsHit += 1;
      this.audio.chime(31, 0.5);
    }
    // Combo tracking: store first bucket, check second for bonus.
    if (isCombo) {
      if (this.currentCueFirstBucket === null) {
        this.currentCueFirstBucket = bucket;
      } else {
        if (this.currentCueFirstBucket !== "miss" && bucket !== "miss") {
          this.scoreEstimate += COMBO_BONUS_CLIENT;
          this.combosHit += 1;
          this.audio.chime(19, 0.3);
        }
        this.currentCueFirstBucket = null;
      }
    }
    this.cb.onHud(this.events.length, bucket, this.scoreEstimate);
    if (this.events.length >= CUES_PER_RUN) this.finish();
  }

  private showCue() {
    if (this.done) return;
    this.currentCueShown = true;
    this.currentCueAcked = false;
    this.currentCueTaps = 0;
    this.currentCueFirstBucket = null;
    this.currentCueShowMs = performance.now() - this.startedAt;
    const cue = this.cues[this.currentCue];
    const [dx, dz] = DIR_VEC[cue.dir];
    this.arrowMesh.position.set(dx * 7, 1.6, dz * 7);
    this.arrowMesh.rotation.set(-Math.PI / 2, 0, Math.atan2(-dx, -dz));
    this.arrowMat.color.setHex(DIR_COLOR[cue.dir]);
    this.arrowMat.emissive.setHex(DIR_EMISSIVE[cue.dir]);
    this.arrowMat.emissiveIntensity = 2.0;
    this.arrowMesh.scale.setScalar(0.001);
    if (this.glyphLine && this.glyphLineMat) {
      this.glyphLine.position.copy(this.arrowMesh.position);
      this.glyphLine.rotation.copy(this.arrowMesh.rotation);
      // v5(c): the line variant has no .emissive — we lift the color
      // brightness instead and rely on the composer's bloom pass to
      // produce the glow halo. Bright lane color keeps the cue salient.
      this.glyphLineMat.color.setHex(DIR_COLOR[cue.dir]);
      this.glyphLineMat.opacity = 1;
      this.glyphLine.scale.setScalar(0.001);
    }
    this.lanes[cue.dir].trackMat.opacity = 0.7;
    // Combo cue: also pre-brighten the second-direction lane to telegraph
    // the chain.
    if (cue.combo_dir !== undefined) {
      this.lanes[cue.combo_dir].trackMat.opacity = 0.45;
    }
  }

  private hideCue() {
    this.arrowMesh.scale.setScalar(0);
    if (this.glyphLine) this.glyphLine.scale.setScalar(0);
    for (const lane of this.lanes) lane.trackMat.opacity = 0.15;
    this.currentCueShown = false;
    this.currentCueTaps = 0;
    this.currentCueFirstBucket = null;
    this.currentCue += 1;
  }

  start() {
    this.audio.arm();
    this.audio.setDroneIntensity(0.4);
    this.startedAt = performance.now();
    this.currentCue = 0;
    this.currentCueShown = false;
    this.currentCueAcked = false;
    this.events = [];
    this.streak = 0;
    this.bestStreak = 0;
    this.scoreEstimate = 0;
    this.standoffsHit = 0;
    this.phrasesCompleted = 0;
    this.phraseStreak = 0;
    this.done = false;
    this.feedbackPulse = 0;
    // v5(d): reset onboarding signals on every start so a replay
    // captures the fresh first-input + modality (rather than
    // inheriting from the prior run).
    this.inputModality = null;
    this.firstInputDelayMs = null;
    this.engine.start();
  }

  private frame(dt: number, _t: number) {
    if (this.done) return;
    if (this.currentCue >= this.cues.length) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    const cue = this.cues[this.currentCue];

    if (!this.currentCueShown && !this.currentCueAcked && runMs >= cue.show_t_ms) {
      this.showCue();
    }
    if (this.currentCueShown && !this.currentCueAcked) {
      const sinceShow = runMs - this.currentCueShowMs;
      if (sinceShow > WINDOW_MS) {
        // Window expired. Record a miss for whichever tap(s) remain.
        // Combo cues with 0 taps record 2 misses; with 1 tap, 1 miss.
        const isCombo = isComboIdx(cue.idx);
        const maxTaps = isCombo ? 2 : 1;
        const remaining = maxTaps - this.currentCueTaps;
        for (let r = 0; r < remaining; r++) {
          this.recordTap(cue.idx, -1, sinceShow, false, isCombo);
        }
        this.currentCueAcked = true;
        this.hideCue();
      }
    }

    // Arrow zoom-in animation: scale from ~0 to 1.4 over the cue window.
    if (this.currentCueShown && !this.currentCueAcked) {
      const sinceShow = runMs - this.currentCueShowMs;
      const ease = Math.min(1, sinceShow / 120);
      const targetScale = 0.6 + ease * 1.0;
      this.arrowMesh.scale.setScalar(targetScale);
      this.arrowMat.emissiveIntensity = 1.5 + ease * 1.5;
      if (this.glyphLine) this.glyphLine.scale.setScalar(targetScale);
    }

    // Feedback pulse decay: drives center node + env light.
    if (this.feedbackPulse > 0) {
      this.feedbackPulse = Math.max(0, this.feedbackPulse - dt * 3.0);
    }
    const breath = 1 + Math.sin(now * 0.004) * 0.05 + this.feedbackPulse * 0.4;
    this.centerNode.scale.setScalar(breath);
    this.centerNode.rotation.y = now * 0.0008;
    this.centerNode.rotation.x = now * 0.0005;
    this.centerMat.emissiveIntensity = 0.6 + this.feedbackPulse * 2.5;
    this.centerMat.emissive.setHex(this.feedbackPulse > 0.05 ? this.feedbackColor : 0x444466);
    // Env light tracks streak — saturates by streak 15.
    this.envLight.intensity = 0.6 + Math.min(1, this.streak / 15) * 1.8;
    this.envLight.color.setHex(this.streak >= 15 ? 0xffd54a : 0x66ddff);
  }

  private finish() {
    this.done = true;
    this.audio.setDroneIntensity(0);
    const bucketsByName: Record<string, number> = { lightning: 0, quick: 0, clean: 0, late: 0, miss: 0 };
    for (const ev of this.events) {
      const correct = ev.dir !== -1 && ev.dir === this.cues[ev.idx].dir;
      const bucket = !correct || ev.latency_ms > WINDOW_MS || ev.latency_ms < 0
        ? "miss"
        : ev.latency_ms <= 120 ? "lightning"
        : ev.latency_ms <= 180 ? "quick"
        : ev.latency_ms <= 240 ? "clean"
        : "late";
      bucketsByName[bucket] += 1;
    }
    this.cb.onGameOver({
      events: this.events,
      scoreEstimate: this.scoreEstimate,
      bestStreak: this.bestStreak,
      bucketsByName,
      combosHit: this.combosHit,
      standoffsHit: this.standoffsHit,
      phrasesCompleted: this.phrasesCompleted,
      inputModality: this.inputModality,
      firstInputDelayMs: this.firstInputDelayMs,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    window.removeEventListener("keydown", this.onKey);
    this.engine.dispose();
  }
}
